// Updated ImageUploader.js with Firebase Storage integration
import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, CheckCircle, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { uploadImageToStorage, deleteImageFromStorage, compressImageForUpload } from '../utils/imageUpload';

function ImageUploader({
  initialImageUrl = '',
  onImageSelect,
  label = 'Image',
  id = 'image-upload',
  accept = 'image/jpeg, image/png',
  maxSizeMB = 5,
  className = '',
  previewHeight = 'max-h-52', // New prop to control preview height
  storageConfig = null // { folderPath: 'brands/logos', brandId: 'brand123' } or null for base64 mode
}) {
  const [imageUrl, setImageUrl] = useState(initialImageUrl);
  const [previewUrl, setPreviewUrl] = useState(initialImageUrl); // Separate state for preview
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);
  
  // Update imageUrl and preview when initialImageUrl prop changes
  useEffect(() => {
    setImageUrl(initialImageUrl);
    setPreviewUrl(initialImageUrl);
  }, [initialImageUrl]);
  
  // Clear states when unmounting
  useEffect(() => {
    return () => {
      // Clean up any object URLs to avoid memory leaks
      if (previewUrl && previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);
  
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };
  
  // Compress and resize the image with more aggressive compression
  const compressImage = async (dataUrl, maxDimension = 1200, initialQuality = 0.7) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        // For very small images, don't compress them further
        const maxSizeInBytes = maxSizeMB * 1024 * 1024;
        if (dataUrl.length < maxSizeInBytes) {
          return resolve(dataUrl);
        }
        
        // Create a canvas element
        const canvas = document.createElement('canvas');
        
        // Calculate new dimensions while maintaining aspect ratio
        let width = img.width;
        let height = img.height;
        
        if (width > height) {
          if (width > maxDimension) {
            height = Math.round(height * (maxDimension / width));
            width = maxDimension;
          }
        } else {
          if (height > maxDimension) {
            width = Math.round(width * (maxDimension / height));
            height = maxDimension;
          }
        }
        
        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;
        
        // Draw image on canvas
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Try progressively lower quality until file size is acceptable
        const compressWithQuality = (quality) => {
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          console.log(`Compressed image with quality ${quality}, size: ${Math.round(compressedDataUrl.length / 1024)}KB`);
          
          if (compressedDataUrl.length > maxSizeInBytes && quality > 0.1) {
            // Reduce quality by a larger step for bigger files
            const nextQuality = quality > 0.5 ? quality - 0.2 : quality - 0.1;
            return compressWithQuality(Math.max(nextQuality, 0.1));
          }
          
          return compressedDataUrl;
        };
        
        const compressedResult = compressWithQuality(initialQuality);
        resolve(compressedResult);
      };
      
      img.onerror = () => {
        reject(new Error('Failed to load image for compression'));
      };
      
      img.src = dataUrl;
    });
  };
  
  const processFile = async (file) => {
    // Reset states
    setError(null);
    setSuccess(false);
    
    // Check if file is an image
    if (!file.type.match('image.*')) {
      setError('Please select an image file');
      return;
    }
    
    // Check if file type is acceptable
    if (accept && !accept.split(',').some(type => file.type.match(type.trim()))) {
      setError(`Please select an image of type: ${accept}`);
      return;
    }
    
    // Check file size (convert MB to bytes)
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes * 2) { // Allow double the final size for compression
      setError(`Image size should be less than ${maxSizeMB * 2}MB for processing`);
      return;
    }
    
    setIsUploading(true);
    
    try {
      // Create a blob URL for preview
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      
      // Create a FileReader to read the file as a data URL
      const reader = new FileReader();
      reader.onload = async (event) => {
        const imageDataUrl = event.target.result;

        try {
          // Compress the image if it's larger than our limit
          let processedImageUrl = imageDataUrl;
          if (imageDataUrl.length > maxSizeBytes) {
            processedImageUrl = await compressImage(imageDataUrl, 1200, 0.7);
            const sizeKB = Math.round(processedImageUrl.length / 1024);
            console.log(`Compressed from ${Math.round(imageDataUrl.length / 1024)}KB to ${sizeKB}KB`);

            // If still too large, show a warning but continue
            if (processedImageUrl.length > maxSizeBytes) {
              console.warn(`Image still large (${sizeKB}KB) after compression`);
            }
          }

          let finalImageUrl = processedImageUrl;

          // If Firebase Storage config is provided, upload to Firebase Storage
          if (storageConfig && storageConfig.folderPath) {
            try {
              console.log(`Uploading to Firebase Storage: ${storageConfig.folderPath}`);

              // Further compress for Firebase Storage to reduce upload time
              const compressedForUpload = await compressImageForUpload(processedImageUrl, 1200, 1200, 0.8);

              // Upload to Firebase Storage
              finalImageUrl = await uploadImageToStorage(
                compressedForUpload,
                storageConfig.folderPath
              );

              console.log(`Firebase Storage upload successful: ${finalImageUrl}`);

              // Delete the old image if this is an update and we have a previous Firebase URL
              if (imageUrl && imageUrl.includes('firebase')) {
                await deleteImageFromStorage(imageUrl);
              }
            } catch (uploadError) {
              console.error('Firebase Storage upload failed:', uploadError);
              setError(`Failed to upload image: ${uploadError.message}`);
              setIsUploading(false);
              return;
            }
          }

          setImageUrl(finalImageUrl);

          // Pass the final URL (Firebase Storage URL or data URL) and file to parent component
          if (onImageSelect) {
            onImageSelect(finalImageUrl, file);
          }

          setSuccess(true);
        } catch (err) {
          console.error("Image processing error:", err);
          setError("Failed to process image. Please try a smaller or different image.");
        } finally {
          setIsUploading(false);
        }
      };
      
      reader.onerror = () => {
        setError('Error reading file');
        setIsUploading(false);
      };
      
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error processing file:', err);
      setError('Failed to process image');
      setIsUploading(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      processFile(file);
    }
  };
  
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      processFile(file);
    }
  };
  
  const handleRemoveImage = async () => {
    // Delete from Firebase Storage if it's a Firebase URL
    if (imageUrl && imageUrl.includes('firebase')) {
      try {
        await deleteImageFromStorage(imageUrl);
        console.log('Image deleted from Firebase Storage');
      } catch (error) {
        console.error('Failed to delete image from Firebase Storage:', error);
        // Continue with removal even if Firebase deletion fails
      }
    }

    // Clean up blob URL if it exists
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }

    setImageUrl('');
    setPreviewUrl('');
    setSuccess(false);
    setError(null);

    // Reset file input value
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Pass empty value to parent component
    if (onImageSelect) {
      onImageSelect('', null);
    }
  };
  
  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };
  
  // CSS classes for the upload area
  const dropzoneClasses = `
    relative border-2 border-dashed rounded-lg p-4 
    flex flex-col items-center justify-center text-center
    transition-all duration-200 ease-in-out
    ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}
    ${error ? 'border-red-300 bg-red-50' : ''}
    ${success && !error ? 'border-green-300 bg-green-50' : ''}
    ${previewUrl ? 'bg-gray-100' : 'bg-white'}
    ${className}
  `;
  
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700">
        {label}
      </label>
      
      <div
        className={dropzoneClasses}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={triggerFileInput}
        style={{ minHeight: '10rem' }}
      >
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          id={id}
          type="file"
          accept={accept}
          onChange={handleFileInputChange}
          className="hidden"
        />
        
        {isUploading ? (
          <div className="flex flex-col items-center justify-center p-4">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mb-2"></div>
            <p className="text-sm text-gray-600">
              {storageConfig ? 'Uploading to cloud storage...' : 'Processing image...'}
            </p>
          </div>
        ) : (
          <>
            {previewUrl ? (
              <div className="relative w-full h-full flex flex-col items-center">
                <div className="relative w-full flex justify-center overflow-hidden">
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className={`${previewHeight} max-w-full object-contain rounded-md mb-2`}
                    onError={(e) => {
                      console.error("Image preview error:", e);
                      setError("Failed to load image preview");
                      e.target.src = "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100' height='100'><rect width='100' height='100' fill='%23eee'/><text x='50%' y='50%' font-size='18' text-anchor='middle' alignment-baseline='middle' fill='%23999'>Image Error</text></svg>";
                    }}
                  />
                </div>
                
                <div className="flex space-x-2 mt-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      triggerFileInput();
                    }}
                    className="text-xs bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 flex items-center"
                  >
                    <Upload size={12} className="mr-1" /> Change
                  </button>
                  
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveImage();
                    }}
                    className="text-xs bg-red-600 text-white px-3 py-1 rounded-md hover:bg-red-700 flex items-center"
                  >
                    <X size={12} className="mr-1" /> Remove
                  </button>
                </div>
                
                {success && !error && (
                  <div className="absolute top-0 right-0 m-2 bg-green-500 text-white rounded-full p-1">
                    <CheckCircle size={16} />
                  </div>
                )}
              </div>
            ) : (
              <div className="py-4 px-2">
                <div className="flex justify-center">
                  <ImageIcon size={36} className="text-gray-400 mb-2" />
                </div>
                <p className="text-sm text-gray-600 mb-1">
                  Drag and drop your image here
                </p>
                <p className="text-xs text-gray-500">
                  or click to browse files
                </p>
                <p className="text-xs text-gray-400 mt-2">
                  JPG/PNG, max {maxSizeMB}MB
                </p>
              </div>
            )}
          </>
        )}
      </div>
      
      {error && (
        <div className="flex items-center mt-1 text-red-600 text-xs">
          <AlertCircle size={12} className="mr-1" />
          {error}
        </div>
      )}
    </div>
  );
}

export default ImageUploader;